import { useEffect } from "react";

// Lightweight SEO head manager — updates document.title + meta tags client-side.
// For production-grade SEO, pre-render via static build or SSR; this keeps us
// dependency-free and still gives per-route meta for link previews.

const upsertMeta = (selector, attrs) => {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
        return el;
    }
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    return el;
};

const upsertLink = (rel, href) => {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
    return el;
};

export default function Seo({ title, description, canonical, jsonLd, ogImage, ogSlug }) {
    useEffect(() => {
        if (title) document.title = title;
        if (description) {
            upsertMeta('meta[name="description"]', { name: "description", content: description });
            upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
        }
        if (title) {
            upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
            upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
        }
        if (canonical) upsertLink("canonical", canonical);

        // Per-page Open Graph image. Falls back to /og/default.png when no
        // explicit slug is provided. Slugs map 1:1 to files generated by
        // /app/backend/scripts/build_og_images.py.
        const origin = typeof window !== "undefined" ? window.location.origin : "";
        const imgPath = ogImage || (ogSlug ? `/og/${ogSlug}.png` : "/og/default.png");
        const absoluteImg = imgPath.startsWith("http") ? imgPath : `${origin}${imgPath}`;
        upsertMeta('meta[property="og:image"]', { property: "og:image", content: absoluteImg });
        upsertMeta('meta[property="og:image:width"]', { property: "og:image:width", content: "1200" });
        upsertMeta('meta[property="og:image:height"]', { property: "og:image:height", content: "630" });
        upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
        upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
        upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: absoluteImg });

        // Support either a single jsonLd object or an array for multi-schema pages
        // (e.g. WebApplication + BreadcrumbList). Each gets its own <script> tag
        // per schema.org guidance.
        const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
        const scriptEls = blocks.map((block) => {
            const el = document.createElement("script");
            el.type = "application/ld+json";
            el.text = JSON.stringify(block);
            el.setAttribute("data-seo-jsonld", "true");
            document.head.appendChild(el);
            return el;
        });
        return () => {
            scriptEls.forEach((el) => {
                if (el.parentNode) el.parentNode.removeChild(el);
            });
        };
    }, [title, description, canonical, jsonLd, ogImage, ogSlug]);
    return null;
}
